"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.propTypes = exports.defaultProps = exports.default = exports.Card = void 0;
var _classnames = _interopRequireDefault(require("classnames"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireDefault(require("react"));
var _actionFormatter = require("../../util/actionFormatter/actionFormatter");
var _getButtonHtml = require("../../util/getButtonHtml/getButtonHtml");
var _getLinkHtml = require("../../util/getLinkHtml/getLinkHtml");
var _renderObject = require("../../util/renderObject/renderObject");
var _CardBody = _interopRequireDefault(require("../CardBody/CardBody"));
var _CardFooter = _interopRequireDefault(require("../CardFooter/CardFooter"));
var _CardHeader = _interopRequireDefault(require("../CardHeader/CardHeader"));
var _LoaderGhostCard = _interopRequireDefault(require("../LoaderGhostCard/LoaderGhostCard"));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
/**
 * Card component
 * @method
 * @param {object} props - React properties passed from composition
 * @returns Card
 */
/**
 * Primary UI component for user interaction
 */
const Card = props => {
  const {
    /* CardHeader content */
    externalMedia,
    image,
    /* CardBody content */
    brandName,
    description,
    eyebrow,
    secondValueTag,
    subDescriptionOne,
    subDescriptionTwo,
    subTitleOne,
    subTitleTwo,
    tag,
    tags,
    tagsTop,
    title,
    titleSupport,
    theme = defaultProps.theme,
    valueTag,
    bulletedList,
    price,
    /* CardFooter content */
    actionGroups,
    externalAction,
    externalActionFullyClick,
    footerStyle,
    /* Card settings */
    actionsGroupBottom,
    backgroundColor,
    contentAlign,
    contentGap,
    contentPadding,
    fullyClickableParent,
    headerPadding,
    hideFooter,
    imgAlign,
    imgGap,
    linkType,
    padding,
    screenReaderText,
    textAlign,
    /* Loading state props */
    isLoading = defaultProps.isLoading,
    loaderVariant
  } = props;
  let formattedActionFullyClick;
  const hasImage = image || externalMedia;
  const showFooter = (actionGroups || externalAction) && linkType !== 'hiddenLink' && !hideFooter;

  // only render CardBody if any one of the body contents is passed as prop
  const hasContent = [brandName, description, eyebrow, secondValueTag, subDescriptionOne, subDescriptionTwo, subTitleOne, subTitleTwo, tag, tags, title, titleSupport, valueTag, bulletedList, price].some(Boolean);
  const hasBodyOrFooter = hasContent || showFooter;
  const paletteOneAction = actionGroups?.actions?.length === 1;
  const hasOneAction = !actionGroups?.actions[0].coupon && (fullyClickableParent || paletteOneAction);
  if (paletteOneAction) {
    formattedActionFullyClick = (0, _actionFormatter.actionFormatter)({
      action: actionGroups?.actions[0],
      types: actionGroups?.types,
      hasOneAction: hasOneAction,
      derivedAriaLabel: screenReaderText,
      fullyClickableParent: paletteOneAction
    }, {
      getButtonMarkup: _getButtonHtml.getButtonHtml,
      getLinkMarkup: _getLinkHtml.getLinkHtml
    });
  }
  const componentClassName = (0, _classnames.default)('pal-c-Card', {
    'pal-c-Card--body': actionsGroupBottom,
    'pal-c-Card--backgroundColor': backgroundColor,
    [`pal-c-Card__contentAlign--${contentAlign}`]: contentAlign,
    [`pal-c-Card__contentGap--${contentGap}`]: contentGap,
    [`pal-c-Card__contentPadding--${contentPadding}`]: contentPadding,
    [`pal-c-Card__imgAlign--${imgAlign}`]: imgAlign,
    [`pal-c-Card__imgGap--${imgGap}`]: imgGap,
    'pal-c-Card--oneAction': hasOneAction,
    [`pal-c-Card__padding--${padding}`]: padding
  });
  const containerStyle = {
    ...(backgroundColor && {
      '--backgroundColor': `var( --pal-colors-${theme}-${backgroundColor} )`
    })
  };
  if (isLoading) {
    return /*#__PURE__*/_react.default.createElement(_LoaderGhostCard.default, {
      variant: loaderVariant
    });
  }
  return /*#__PURE__*/_react.default.createElement("div", {
    className: componentClassName,
    style: containerStyle
  }, hasOneAction && /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, !externalActionFullyClick && formattedActionFullyClick && /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, (0, _renderObject.renderObject)(formattedActionFullyClick)), externalActionFullyClick && /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, (0, _renderObject.renderObject)(externalActionFullyClick))), hasImage && /*#__PURE__*/_react.default.createElement(_CardHeader.default, {
    tagsTop: tagsTop,
    externalMedia: externalMedia,
    headerPadding: headerPadding,
    image: {
      desktopImage: image?.image,
      tabletImage: image?.tabletImage,
      mobileImage: image?.mobileImage,
      url: image?.url,
      ratio: image?.ratio,
      altText: image?.altText,
      loading: image?.loading
    }
  }), hasBodyOrFooter && /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-Card--content"
  }, /*#__PURE__*/_react.default.createElement(_CardBody.default, {
    brandName: {
      text: brandName?.text,
      textStyle: brandName?.textStyle,
      tagName: brandName?.tagName,
      textColor: brandName?.textColor
    },
    bulletedList: bulletedList,
    contentAlign: contentAlign,
    contentGap: contentGap,
    description: {
      text: description?.text,
      textStyle: description?.textStyle,
      textColor: description?.textColor
    },
    eyebrow: eyebrow,
    linkType: linkType,
    price: price,
    secondValueTag: secondValueTag,
    subTitleOne: {
      text: subTitleOne?.text,
      tagName: subTitleOne?.tagName,
      textColor: subTitleOne?.textColor
    },
    subDescriptionOne: {
      text: subDescriptionOne?.text,
      textStyle: subDescriptionOne?.textStyle,
      textColor: subDescriptionOne?.textColor
    },
    subTitleTwo: {
      text: subTitleTwo?.text,
      tagName: subTitleTwo?.tagName,
      textColor: subTitleTwo?.textColor
    },
    subDescriptionTwo: {
      text: subDescriptionTwo?.text,
      textStyle: subDescriptionTwo?.textStyle,
      textColor: subDescriptionTwo?.textColor
    },
    tag: tag,
    tags: tags,
    textAlign: textAlign,
    title: {
      text: title?.text,
      textStyle: title?.textStyle,
      tagName: title?.tagName,
      textColor: title?.textColor
    },
    titleSupport: titleSupport,
    valueTag: valueTag
  }), showFooter && /*#__PURE__*/_react.default.createElement(_CardFooter.default, _extends({}, actionGroups, {
    alignment: textAlign,
    externalAction: externalAction,
    linkType: linkType,
    style: footerStyle
  }))));
};

/**
 * Property type definitions
 * @type {object}
 * @property {object} actionGroups - Allows passing an object to the actionGroup prop
 * @property {boolean} actionsGroupBottom - Pushes the footer (action group) to the bottom of the card and body will take up all available space
 * @property {string} backgroundColor - Sets the backgroundColor of the card
 * @property {object} brandName - Allows brand prop to accept an object (text, tagName, titleStyle)
 * @property {array} bulletedList - array of bulleted list items
 * @property {string} contentAlign - Where the content block should align vertically ('top', 'middle', 'bottom')
 * @property {string} contentGap - The space between the body content and the action group ( 'none', 'sm', 'md', 'lg', 'xl' )
 * @property {string} contentPadding - The padding for the content of the card
 * @property {object} description - Allows description prop to accept an object (text, titleStyle)
 * @property {string|object} eyebrow - Smaller text above the title (string or { text, textColor })
 * @property {object} externalAction - Accepts actions to render from web-site with business logic
 * @property {object} externalActionFullyClick - Accept fully clickable absolute action from web-site
 * @property {boolean} fullyClickableParent - Set to true if the card can be fully clickable
 * @property {string} headerPadding - The padding between the header and the image ('none', 'compact', 'default')
 * @property {boolean} hideFooter - Shows/Hides the footer
 * @property {object} image - Allows header prop to accept an image object
 * @property {string} imgGap - The space between image and the content group ('none', 'sm', 'md', 'lg', 'xl')
 * @property {string} linkType - linkType for the hidden link on ArticleCard
 * @property {string} padding - The space between the edge of the card and the content ('none', 'sm', 'md', 'lg')
 * @property {string} secondValueTag - Text for the second value tag
 * @property {object} subDescriptionOne - Allows subDescriptionOne to accept an object ('textStyle' , 'text')
 * @property {object} subDescriptionTwo - Allows subDescriptionTwo to accept an object ('textStyle' , 'text')
 * @property {object} subTitleOne - Allows subTitleOne prop to accept an object ('text', 'children', 'tagName')
 * @property {object} subTitleTwo - Allows subTitleOne prop to accept an object ('text', 'children', 'tagName')
 * @property {object} price -  Allows price prop to accept an object to render Price component on CardBody
 * @property {object} tag - Allows passing an object to the tag prop
 * @property {array} tags - Allows passing an arra of tag objects to render on card body
 * @property {string} textAlign - Where the text content should align horizontally ('left', 'center', 'right')
 * @property {object} title - Allows title prop to accept an object ('text', 'tagName', 'titleStyle')
 * @property {string} titleSupport - Support text for title
 * @property {string} valueTag - Text for value tag
 * @property {array} tagsTop - Allows passing an array of tag objects to display in the card header
 * @property {boolean} isLoading - Sets the loading state of the card
 * @property {string} loaderVariant - Sets the variant for LoaderGhostCard when loading is set to true
 */
exports.Card = Card;
const propTypes = exports.propTypes = {
  /**
   * Allows passing an object to the actionGroup prop
   */
  actionGroups: _propTypes.default.object,
  /**
   * Pushes the footer (action group) to the bottom of the card and body will take up all available space
   */
  actionsGroupBottom: _propTypes.default.bool,
  /**
   * Sets the backgroundColor of the card
   */
  backgroundColor: _propTypes.default.string,
  /**
   * Allows brandName prop to accept an object
   * - ** text: ** Sets the text of the brand
   * - ** tagName: ** Sets the HTML tag of the brand oneOf( ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] )
   * - ** titleStyle: ** Sets the style of the brand oneOf( ['body-lg', 'body', 'body-sm', 'label', 'label-sm', 'title-xlg', 'title-lg', 'title', 'title-sm', 'headline-lg', 'headline', 'headline-sm', 'display-xlg', 'display-lg', 'display', 'display-sm', 'displayBody', 'displayBody-sm',] )
   */
  brandName: _propTypes.default.object,
  /**
   * array of bulleted list items
   */
  bulletedList: _propTypes.default.array,
  /**
   * Where the content block should align vertically
   * - **top:** top of available space
   * - **middle:** middle of available space
   * - **bottom:** bottom of available space
   */
  contentAlign: _propTypes.default.oneOf(['top', 'middle', 'bottom']),
  /**
   * The space between the body content and the action group
   * - **none:** 0 Sets no spacing
   * - **sm:** 8px Sets 8px spacing
   * - **md:** 16px Sets 16px spacing
   * - **lg:** 24px Sets 24px spacing
   * - **xl:** 32px Sets 32px spacing
   */
  contentGap: _propTypes.default.oneOf(['none', 'sm', 'md', 'lg', 'xl']),
  /**
   * The padding for the content of the card
   * option is not set, padding is 0
   * - **none:** 0 Sets no padding
   * - **default:** Sets to 16px on the sides and 8px on the bottom
   */
  contentPadding: _propTypes.default.oneOf(['none', 'default']),
  /**
   * Allows description prop to accept an object
   */
  description: _propTypes.default.object,
  /**
   * Smaller text above the title
   * - string: eyebrow text
   * - object: { text, textColor }
   */
  eyebrow: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.shape({
    text: _propTypes.default.string,
    textColor: _propTypes.default.string
  })]),
  /**
   * Accepts actions to render from web-site with business logic
   */
  externalAction: _propTypes.default.object,
  /**
   * Accept fully clickable absolute action from web-site
   */
  externalActionFullyClick: _propTypes.default.object,
  /**
   * Set to true if the card can be fully clickable
  */
  fullyClickableParent: _propTypes.default.bool,
  /**
   * The padding between the header and the image
   * option is not set, padding is 0
   * - **none:** 0 Sets no padding
   * - **compact:**
   * - **default:** 32px Sets 32px spacing on XL screen and 24 sides LG-SM
   */
  headerPadding: _propTypes.default.oneOf(['none', 'compact', 'default']),
  /**
   * Shows/Hides the footer
   */
  hideFooter: _propTypes.default.bool,
  /**
   * Allows header prop to accept an object
   * - **imgRatio:** oneOf( ['1x1', '4x5', '5x4', '9x16', '16x9'] )
   * - **imgAlt:** Sets the alt text for the image
   * - **imgSrc:** Sets the URL for the image
   * - **contextTag:** Sets the context tag for the image
   * - **text:** Sets the text for the image
   * - **bookmark:** Sets the bookmark for the image
   * - **addToCart:** Sets the add to cart for the image
   * - **padding:** Sets the padding for the image oneOf(['sm', 'lg'])
   */
  image: _propTypes.default.object,
  /**
   * Where in the card the image aligns
   * - **top:** Aligns the image to the top of card
   * - **right:** Aligns the image to the right of card
   * - **bottom:** Aligns the image to the bottom of card
   * - **left:** Aligns the image to the left of card
   */
  imgAlign: _propTypes.default.oneOf(['top', 'right', 'bottom', 'left']),
  /**
   * The space between image and the content group
   * option is not set, padding is 0
   * - **none:** 0 Sets no spacing
   * - **sm:** 8px Sets 8px spacing
   * - **md:** 16px Sets 16px spacing
   * - **lg:** 24px Sets 24px spacing
   * - **xl:** 32px Sets 32px spacing
   */
  imgGap: _propTypes.default.oneOf(['none', 'sm', 'md', 'lg', 'xl']),
  /**
   * linkType for the card
   */
  linkType: _propTypes.default.string,
  /**
   * The space between the edge of the card and the content
   * option is not set, padding is 0
   * - **none:** 0 Sets no padding
   * - **sm:** 16px Sets 16px padding
   * - **md:** 24px Sets 24px padding
   * - **lg:** 32px Sets 32px padding
   */
  padding: _propTypes.default.oneOf(['none', 'sm', 'md', 'lg']),
  /**
   * Allows price prop to accept an object to render Price component on CardBody
   * See price component for more detail ../Price/Price.jsx
   */
  price: _propTypes.default.object,
  /**
   * Text for the second value tag
   */
  secondValueTag: _propTypes.default.string,
  /**
   * Accepts subDescriptionOne as an object
   * - ** textStyle: ** Sets the style of the text
   * - ** text: ** String to set as the text
   */
  subDescriptionOne: _propTypes.default.object,
  /**
   * Accepts subDescriptionTwo as an object
   * - ** textStyle: ** Sets the style of the text
   * - ** text: ** String to set as the text
   */
  subDescriptionTwo: _propTypes.default.object,
  /**
   * Allows subTitleOne prop to accept an object
   * - ** text: ** Sets the text for the subTitleOne
   * - ** children: ** Sets the children for the subTitleOne
   * - ** tagName: ** Sets the HTML tag for the subTitleOne oneOf( ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] )
   */
  subTitleOne: _propTypes.default.object,
  /**
   * Allows subTitleTwo prop to accept an object
   * - ** text: ** Sets the text for the subTitleTwo
   * - ** children: ** Sets the children for the subTitleTwo
   * - ** tagName: ** Sets the HTML tag for the subTitleTwo oneOf( ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] )
   */
  subTitleTwo: _propTypes.default.object,
  /**
   * Allows passing an object to the tag prop
   * See tag component for more detail ../Tag/Tag.jsx
   */
  tag: _propTypes.default.object,
  /**
   * Allows passing array of tag objects
   * See tag component for more detail ../Tag/Tag.jsx
   */
  tags: _propTypes.default.arrayOf(_propTypes.default.object),
  /**
   * Where the text content should align horizontally
   * - **left:** left side of available space
   * - **center:** center of available space
   * - **right:** right of available space
   */
  textAlign: _propTypes.default.oneOf(['left', 'center', 'right']),
  /**
   * Sets the theme for the backgroundColor
   */
  theme: _propTypes.default.string,
  /**
   * Allows title prop to accept an object
   * - ** text: ** Sets the title of the header
   * - ** children: ** Sets the children of the header
   * - ** tagName: ** Sets the HTML tag of the header oneOf( ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] )
   * - ** titleStyle: ** Sets the style of the header oneOf( ['body-lg', 'body', 'body-sm', 'label', 'label-sm', 'title-xlg', 'title-lg', 'title', 'title-sm', 'headline-lg', 'headline', 'headline-sm', 'display-xlg', 'display-lg', 'display', 'display-sm', 'displayBody', 'displayBody-sm',] )
   */
  title: _propTypes.default.object,
  /**
   * Support text for title
   */
  titleSupport: _propTypes.default.string,
  /**
   * Text for value tag
   */
  valueTag: _propTypes.default.string,
  /**
   * Allows passing an array of tag objects to the CardHeader
   * See tag component for more detail ../Tag/Tag.jsx
   */
  tagsTop: _propTypes.default.arrayOf(_propTypes.default.object),
  /**
   *  Loading state of the card
  */
  isLoading: _propTypes.default.bool,
  /**
   * Sets the variant of the LoaderGhostCard when loading is set to true
   */
  loaderVariant: _propTypes.default.oneOf(['textRectangle', 'textCircle', 'default'])
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {string} backgroundColor='background-default' - sets the background color
 * @property {string} theme='default' - sets the theme at the component level
 * @property {boolean} isLoading=false - sets the default loading state for the card
 */
const defaultProps = exports.defaultProps = {
  backgroundColor: 'background-default',
  theme: 'default',
  isLoading: false
};
Card.propTypes = propTypes;
var _default = exports.default = Card;